/**  React libraries */
import React, { useEffect, useMemo, useState } from "react";
import { Col, FormControl, Row, Table } from 'react-bootstrap';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import { dateConversion } from "../../utils/dateHandler";

/**  Custom components stores services types constant */
import { AppDispatch, RootState } from "../../store/store";
import { CertificatesConstants, CertificateData } from "./certificates.types";
import { fetchCertificates } from "./certificatesSlice";
import Loader from '../../components/Loader/Loader';
import ErrorMessage from '../../components/Error-Message/Error-Message';
import { STORAGE_KEYS, StorageService } from "../../utils/storageService";

/** Images */
import calendarIcon from "../../assets/images/calendarIcon.svg";
import searchIcon from "../../assets/images/searchicon.svg";
import SearchInput from "../../components/SearchInput/SearchInput";



const CertificatesList: React.FC = () => {
    const { certificates, certificatesLoading, certificatesError } = useSelector((state: RootState) => state.certificates);
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const [currentPage, setCurrentPage] = useState<number>(1);

    const [showToaster, setTosterShow] = useState(false);
    const [toasterInfo, setToasterInfo] = useState<{ type: "success" | "error" | "warning", message: string }>({ type: "success", message: "" });

    const [searchValue, setSearchValue] = useState<string>("");
    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [endDate, setEndDate] = useState<Date | undefined>(undefined);

    useEffect(() => {
        if (certificates.length == 0) {
            const loadCertificates = async () => {
                const resultAction = await dispatch(fetchCertificates());
                if (fetchCertificates.fulfilled.match(resultAction)) {
                    const certificates = resultAction.payload;
                    // If single certificate are present then navigate to logo request page
                    if (certificates.length === 1) {
                        StorageService.set(STORAGE_KEYS.CERTIFICATE, certificates[0]);
                        navigate(`/dashboard/${certificates[0].id}`);
                        return;
                    } else if (certificates.length == 0) {
                        console.warn("No Certificates found.");
                        return;
                    }
                }
            };
            // Load certificates
            loadCertificates();
        } else if (certificates.length === 1) {
            navigate(`/dashboard/${certificates[0].id}`);
            return;
        }
    }, []);

    /**
     * Filter by date range
     * @param certificateItem 
     * @returns boolean
     */
    const filterByDate = (certificateItem: CertificateData) => {
        try {
            const submittedDate = new Date(certificateItem.validTo);
            if (!startDate && !endDate) return true;
            if (startDate && endDate) {
                return submittedDate >= startDate && submittedDate <= endDate;
            }
            if (startDate) {
                return submittedDate >= startDate;
            }
            if (endDate) {
                return submittedDate <= endDate;
            }
            return true;
        } catch (exe) {
            console.log(exe);
            return false;
        }
    };

    /**
     * Filter certificate list as per search and date filter
     */
    const filteredCertificateList = useMemo(() => {
        try {
            return certificates.filter((certificateItem: CertificateData) =>
                certificateItem.certificateId.toLowerCase().includes(searchValue.toLowerCase())
            )
                .filter(filterByDate);
        } catch (exe) {
            console.log(exe);
            return [];
        }
    }, [certificates,searchValue]);

    const totalPages = Math.ceil(filteredCertificateList.length / CertificatesConstants.DATA_PER_PAGE);
    const startIdx = (currentPage - 1) * CertificatesConstants.DATA_PER_PAGE;
    const endIdx = Math.min(startIdx + CertificatesConstants.DATA_PER_PAGE, filteredCertificateList.length);
    const certificateListPageData = filteredCertificateList.slice(startIdx, endIdx);


    /**
     * Pagination open page to
     * @param page 
     */
    const goToPage = (page: number) => {
        setCurrentPage(page);
    }

    /**
   * On Seach field key up
   * @param searchText 
   * @returns 
   */
    const onSeachFieldKeyUp = (searchText: string) => {
        setCurrentPage(1);
        setSearchValue(searchText);
    }

    /**
     * On Certificate Item Click
     * @param certificateItem 
     */
    const onCertificateListItemClick = (certificateItem: CertificateData) => {
        try {
            StorageService.set(STORAGE_KEYS.CERTIFICATE, certificateItem);
            navigate(`/dashboard/${certificateItem.id}`);
        } catch (exe) {
            console.log(exe)
        }
    }

    /**
     * Handle date change
     * @param dates 
     */
    const handleDateChange = (dates: [Date | null, Date | null]) => {
        try {
            const [start, end] = dates;
            if (!start && !end) {
                setStartDate(undefined);
                setEndDate(undefined);
            } else {
                setStartDate(start || undefined);
                setEndDate(end || undefined);
            }
        } catch (exe) {
            console.log(exe);
        }

    };

    /**
     * Format date
     * @param date 
     * @returns formatted date
     */
    const formatDate = (date: Date | undefined): string => {
        try {
            if (!date) return "";
            const day = date.getDate().toString().padStart(2, "0");
            const month = (date.getMonth() + 1).toString().padStart(2, "0");
            const year = date.getFullYear();
            return `${day}. ${month}. ${year}`;
        } catch (exe) {
            console.log(exe);
            return " ";
        }
    };

    /**
     * Format Date range
     */
    const formattedDateRange =
        startDate && endDate
            ? `(${formatDate(startDate)} - ${formatDate(endDate)})`
            : startDate
                ? `(${formatDate(startDate)} - )`
                : "";

    // Display loader while fetching data 
    if (certificatesLoading) {
        return <Loader />;
    }

    // Display Error while fetching data 
    if (certificatesError) {
        return <ErrorMessage message={certificatesError} />
    }

    return (
        <>
            <div className="bg-white px-4 pb-4 rounded shadow-sm">
                { }
                <Row className="bg-white pt-3 pb-3 px-4">
                    <Col xl={9} className="mb-lg-3 mb-xl-0">
                        <h4 className="fs-22 fw-bold mb-3">Certificates</h4>
                    </Col>
                    <Col xl={3}>
                        <div className="d-flex flex-column flex-md-row justify-content-md-end justify-content-start gap-3">
                            <SearchInput value={searchValue} onChange={setSearchValue} placeholder="Search by Certificate ID" />
                            {/*
                               <div className="date-picker-container">
                                <DatePicker
                                    selected={startDate}
                                    onChange={handleDateChange}
                                    selectsRange
                                    startDate={startDate}
                                    endDate={endDate}
                                    className="form-control custom-datepicker"
                                    placeholderText="Select Date"
                                    dateFormat="MM/dd/yyyy"
                                    value={formattedDateRange}
                                />
                                <img
                                    src={calendarIcon}
                                    alt="Calendar Icon"
                                    className="position-absolute "
                                    style={{
                                        width: "15px",
                                        height: "16.67px",
                                        transform: "translateY(-63%)",
                                        right: "0px",
                                        marginRight: "5px",
                                        top: "53%",
                                    }}
                                />
                            </div> 
                            */}
                        </div>
                    </Col>
                </Row>
                <div className="table-responsive mt-1">
                    <Table hover className="text-center align-middle">
                        <colgroup>
                            <col style={{ width: "50%" }} />
                            <col style={{ width: "30%" }} />
                            <col style={{ width: "20%" }} />
                        </colgroup>
                        <thead>
                            <tr>
                                {CertificatesConstants.CERTIFICATE_LIST_HEADER.map((header, index) => (
                                    <th key={index} className="request-queue-table-header">
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {certificateListPageData.length === 0 ? (
                                <tr>
                                    <td colSpan={8} style={{ textAlign: "center" }}>
                                        No Data Found
                                    </td>
                                </tr>
                            ) : (
                                certificateListPageData.map((item: CertificateData, index: number) => (
                                    <tr style={{ cursor: 'pointer' }} key={index} onClick={() => onCertificateListItemClick(item)}>
                                        <td style={{ fontSize: "12px", color: "#353535" }}>
                                            {item.accountName}
                                        </td>
                                        <td style={{ fontSize: "12px", color: "#353535" }}>
                                            {item.certificateId}
                                        </td>
                                        <td style={{ fontSize: "12px", color: "#353535" }}>
                                            {dateConversion(item.validTo, "MMMM d, yyyy")}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
                {filteredCertificateList.length > CertificatesConstants.DATA_PER_PAGE && (
                    <div className="d-flex align-items-center justify-content-center">
                        <p className="mb-0 text-secondary">
                            Showing {startIdx + 1} to {endIdx} of {filteredCertificateList.length}{" "}
                            entries
                        </p>
                        <div className="ms-3">
                            {Array.from({ length: totalPages }, (_, index) => (
                                <button type="button"
                                    key={index}
                                    onClick={() => goToPage(index + 1)}
                                    className={`btn btn-sm rounded-circle me-1 pagination-button ${currentPage === index + 1
                                        ? "paginationButton text-white"
                                        : "btn-outline-secondary"
                                        }`}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default CertificatesList;